import { useState, useCallback } from "react";

export interface AssistantCharacter {
  id: string;
  name: string;
  role: string;
  description: string;
  emoji: string;
  avatar?: string;
  personality: string;
  /** Frases de abertura rotativas */
  greetings: string[];
  /** Frases de espera/idle */
  idlePhrases: string[];
  /** Estilo visual do balão */
  bubbleStyle: {
    bg: string;
    border: string;
    text: string;
    accent: string;
  };
}

export const ASSISTANT_CHARACTERS: AssistantCharacter[] = [
  {
    id: "ferrugini",
    name: "Ferrugini",
    role: "Sócio Gestor",
    description: "Sócio fundador. Especialista em direito tributário e estratégia jurídica.",
    emoji: "⚖️",
    avatar: "/src/assets/alencar-ferrugini.jpg",
    personality: "simpático e brincalhão",
    greetings: [
      "Precisa de um conselho? 😄",
      "Passa na minha sala para uma cervejinha! 🍺",
      "Oi! O chefe chegou. Pode falar.",
      "Vem cá, tenho uma dica boa pra você!",
    ],
    idlePhrases: [
      "Tô aqui, não precisa ter medo! 😄",
      "Pode perguntar, eu não mordo.",
      "Na minha época era tudo mais difícil… mas vou te ajudar mesmo assim.",
    ],
    bubbleStyle: {
      bg: "bg-primary/10",
      border: "border-primary/30",
      text: "text-primary",
      accent: "bg-primary",
    },
  },
  {
    id: "carla",
    name: "Carla",
    role: "Advogada Trabalhista",
    description: "Advogada trabalhista. Especialista em relações de trabalho e eSocial.",
    emoji: "😱",
    personality: "empática e desesperada",
    greetings: [
      "Meu Deus, você está bem?! 😰",
      "Tenho um babado pra te contar depois! 🤫",
      "Se o Alencar souber disso…",
      "Ai, que sufoco! O que aconteceu?",
    ],
    idlePhrases: [
      "Tô com o coração na mão aqui!",
      "Qualquer coisa me chama, antes que seja tarde!",
      "Já rezei três vezes hoje… e ainda bem!",
    ],
    bubbleStyle: {
      bg: "bg-rose-50",
      border: "border-rose-300",
      text: "text-rose-700",
      accent: "bg-rose-500",
    },
  },
  {
    id: "victor",
    name: "Victor",
    role: "O Gênio",
    description: "Advogado cível. Focado em contratos, petições e direito civil.",
    emoji: "🧐",
    personality: "sério e nada humilde",
    greetings: [
      "Você estudou para me dizer isso? 🤨",
      "Eu sei de tudo, mas vou te ajudar hoje.",
      "Hmm… deixa eu ver se é digno da minha atenção.",
      "Pode falar. Mas seja preciso, por favor.",
    ],
    idlePhrases: [
      "Estou contemplando a perfeição do direito.",
      "Não me interrompa — estou a pensar.",
      "É claro que eu já sabia disso.",
    ],
    bubbleStyle: {
      bg: "bg-[#a9abae]/10",
      border: "border-slate-400",
      text: "text-slate-700",
      accent: "bg-slate-600",
    },
  },
  {
    id: "rodolpho",
    name: "Rodolpho",
    role: "O Tranquilão",
    description: "Especialista em auditoria fiscal e recuperação de créditos tributários.",
    emoji: "🌺",
    personality: "relaxado e descontraído",
    greetings: [
      "Café ou chá? ☕",
      "Não fui eu que perdi o arquivo! 😅",
      "Bora no boteco raiz no Japão? 🍻",
      "Tudo tranquilo por aqui. E aí?",
    ],
    idlePhrases: [
      "Deixa rolar, vai dar certo!",
      "Não esquenta, tô aqui.",
      "Hora do café? Sempre!",
    ],
    bubbleStyle: {
      bg: "bg-amber-50",
      border: "border-amber-300",
      text: "text-amber-700",
      accent: "bg-amber-500",
    },
  },
  {
    id: "tininha",
    name: "Tininha",
    role: "Pincher Raivosa",
    description: "Assistente administrativa. Organiza prazos, documentos e rotinas do escritório.",
    emoji: "🐕",
    personality: "brava e latejante",
    greetings: [
      "AU! AU! 🐾 Você chegou!",
      "Vou te morder se não aprender a mexer no sistema! 😤",
      "AU AU AU! Fica quieto e me deixa trabalhar!",
      "Grrr… o que você quer agora?!",
    ],
    idlePhrases: [
      "AU! Tô de olho em você!",
      "Não me testa que eu mordo mesmo!",
      "GRRRR… chega mais.",
    ],
    bubbleStyle: {
      bg: "bg-orange-50",
      border: "border-orange-400",
      text: "text-orange-700",
      accent: "bg-orange-500",
    },
  },
  {
    id: "so_moura",
    name: "Sô Moura",
    role: "Gente Boa",
    description: "Consultor previdenciário. Especialista em aposentadorias e benefícios do INSS.",
    emoji: "😂",
    personality: "piadista e bem-humorado",
    greetings: [
      "Histórias do Alencar… você quer ouvir? 🤣",
      "Antônio e Augusto mandaram abraço! 👋",
      "Vou aumentar o aluguel se não rir! 😂",
      "Ei! Sabe qual é a piada favorita do Sô Moura?",
    ],
    idlePhrases: [
      "Tô aqui, pronto pra alegrar o dia!",
      "O Alencar ia adorar essa situação…",
      "Não paga aluguel e ainda quer ajuda? Brincadeira! 😂",
    ],
    bubbleStyle: {
      bg: "bg-green-50",
      border: "border-green-300",
      text: "text-green-700",
      accent: "bg-green-500",
    },
  },
];

export interface AssistantSettings {
  enabled: boolean;
  characters: Record<string, boolean>;
}

const STORAGE_KEY = "assistant_settings";

const defaultSettings: AssistantSettings = {
  enabled: false,
  characters: Object.fromEntries(ASSISTANT_CHARACTERS.map((c) => [c.id, false])),
};

function loadSettings(): AssistantSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSettings;
    const parsed = JSON.parse(raw);
    return {
      enabled: parsed.enabled ?? false,
      characters: {
        ...defaultSettings.characters,
        ...(parsed.characters ?? {}),
      },
    };
  } catch {
    return defaultSettings;
  }
}

function saveSettings(settings: AssistantSettings) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

export function useAssistantSettings() {
  const [settings, setSettings] = useState<AssistantSettings>(loadSettings);

  const setGlobalEnabled = useCallback((enabled: boolean) => {
    setSettings((prev) => {
      const next = { ...prev, enabled };
      saveSettings(next);
      return next;
    });
  }, []);

  const setCharacterEnabled = useCallback((id: string, value: boolean) => {
    setSettings((prev) => {
      const next = {
        ...prev,
        characters: { ...prev.characters, [id]: value },
      };
      saveSettings(next);
      return next;
    });
  }, []);

  const enableAll = useCallback(() => {
    setSettings((prev) => {
      const next = {
        enabled: true,
        characters: Object.fromEntries(ASSISTANT_CHARACTERS.map((c) => [c.id, true])),
      };
      saveSettings(next);
      return next;
    });
  }, []);

  const disableAll = useCallback(() => {
    setSettings((prev) => {
      const next = {
        enabled: false,
        characters: Object.fromEntries(ASSISTANT_CHARACTERS.map((c) => [c.id, false])),
      };
      saveSettings(next);
      return next;
    });
  }, []);

  return {
    settings,
    setGlobalEnabled,
    setCharacterEnabled,
    enableAll,
    disableAll,
  };
}
